import React, { useContext, useEffect, useReducer, useState } from 'react';
import { ShortcutContext } from 'Context/Shortcut';
import useShortcut from 'Hooks/useShortcut';
import useShortcutScope from 'Hooks/useShortcutScope';
import useFormShortcuts from 'Hooks/useFormShortcuts';
import { formatCombo } from 'utils/platform';
import {
  Overlay, Panel, Title, Section, SectionLabel, List, Row, Label, Keys, Key, Or, Empty
} from './styled';

// Several combos can trigger the same action (mod+S and mod+Enter both
// save) - group them into one row instead of listing the action twice.
const groupByDescription = (shortcuts) => {
  const order = [];
  const combosByDescription = new Map();

  shortcuts.forEach(({ combo, description }) => {
    if (!combosByDescription.has(description)) {
      combosByDescription.set(description, []);
      order.push(description);
    }
    combosByDescription.get(description).push(combo);
  });

  return order.map((description) => ({ description, combos: combosByDescription.get(description) }));
};

const ShortcutRow = ({ description, combos }) => (
  <Row>
    <Label>{ description }</Label>
    <Keys>
      { combos.map((combo, index) => (
        <React.Fragment key={ combo }>
          { index > 0 && <Or>or</Or> }
          <Key>{ formatCombo(combo) }</Key>
        </React.Fragment>
      )) }
    </Keys>
  </Row>
);

const ShortcutSection = ({ label, groups }) => {
  if (groups.length === 0) return null;

  return (
    <Section>
      <SectionLabel>{ label }</SectionLabel>
      <List>
        { groups.map((group) => <ShortcutRow key={ group.description } { ...group } />) }
      </List>
    </Section>
  );
};

const ShortcutsHelp = () => {
  const shortcutContext = useContext(ShortcutContext);
  const [isOpen, setIsOpen] = useState(false);
  const scope = useShortcutScope('shortcuts-help', isOpen);

  const [, forceRefresh] = useReducer((c) => c + 1, 0);

  useShortcut('mod+/', () => setIsOpen((prev) => !prev), { description: 'Show keyboard shortcuts' });
  useFormShortcuts({ onCancel: () => setIsOpen(false), scope, cancelLabel: 'Close' });

  useEffect(() => {
    if (isOpen) forceRefresh();
  }, [isOpen]);

  if (!isOpen) return null;

  const shortcuts = shortcutContext?.getActiveShortcuts() || [];
  const screenGroups = groupByDescription(shortcuts.filter((shortcut) => !shortcut.isGlobal));
  const generalGroups = groupByDescription(shortcuts.filter((shortcut) => shortcut.isGlobal));

  return (
    <Overlay onClick={ () => setIsOpen(false) }>
      <Panel onClick={ (event) => event.stopPropagation() }>
        <Title>Keyboard shortcuts</Title>
        <ShortcutSection label="This screen" groups={ screenGroups } />
        <ShortcutSection label="General" groups={ generalGroups } />
        { shortcuts.length === 0 && <Empty>No shortcuts available here.</Empty> }
      </Panel>
    </Overlay>
  );
};

export default ShortcutsHelp;
